import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, MessageSquare, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import NotificationBell from './NotificationBell';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Poll unread messages every 30s so the badge stays reasonably fresh.
  // Cheap count() query, indexed on receiver_id + is_read.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/messages/unread-count');
        if (!cancelled) setUnreadMessages(res.data?.count || 0);
      } catch {
        /* silent, badge just stays stale */
      }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/z-control';
    if (user.role === 'provider') return '/provider/dashboard';
    return '/dashboard';
  };

  return (
    <header className="sticky top-0 z-50 glass-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <span className="font-heading text-2xl font-semibold text-[#1A1A1A]">ZURIYA</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/discover" className="text-stone-600 hover:text-[#1A1A1A] transition-colors font-medium" data-testid="nav-discover">
              Discover
            </Link>
            <Link to="/feed" className="text-stone-600 hover:text-[#1A1A1A] transition-colors font-medium" data-testid="nav-feed">
              Feed
            </Link>
            {user?.role === 'provider' && (
              <Link to="/provider/dashboard" className="text-stone-600 hover:text-[#1A1A1A] transition-colors font-medium" data-testid="nav-provider-dashboard">
                Dashboard
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-stone-600 hover:text-[#1A1A1A] transition-colors font-medium" data-testid="nav-admin">
                Admin
              </Link>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <NotificationBell />
                <Link to="/messages" data-testid="nav-messages">
                  <Button variant="ghost" size="icon" className="text-stone-600 hover:text-[#1A1A1A] relative">
                    <MessageSquare className="h-5 w-5" />
                    {unreadMessages > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
                        data-testid="nav-messages-badge"
                        aria-label={`${unreadMessages} unread`}
                      >
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-medium">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-stone-700 font-medium">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Messages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-stone-600 hover:text-[#1A1A1A]" data-testid="nav-login">
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary rounded-full" data-testid="nav-register">
                    Sign up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Auth Actions (bell + hamburger) */}
          <div className="md:hidden flex items-center gap-1">
            {user && <NotificationBell />}
            <button 
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200/50">
            <nav className="flex flex-col gap-4">
              <Link to="/discover" className="text-stone-600 hover:text-[#1A1A1A] py-2" onClick={() => setMobileMenuOpen(false)}>
                Discover
              </Link>
              <Link to="/feed" className="text-stone-600 hover:text-[#1A1A1A] py-2" onClick={() => setMobileMenuOpen(false)}>
                Feed
              </Link>
              {user ? (
                <>
                  <Link to={getDashboardLink()} className="text-stone-600 hover:text-[#1A1A1A] py-2" onClick={() => setMobileMenuOpen(false)}>
                    Dashboard
                  </Link>
                  <Link to="/messages" className="text-stone-600 hover:text-[#1A1A1A] py-2" onClick={() => setMobileMenuOpen(false)}>
                    Messages
                  </Link>
                  <button onClick={handleLogout} className="text-left text-red-600 py-2">
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-stone-600 hover:text-[#1A1A1A] py-2" onClick={() => setMobileMenuOpen(false)}>
                    Log in
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="btn-primary w-full">Sign up</Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
